import { useEffect, useState } from "react";
import { Link } from "react-router";
import EmailForm from "../components/email-form";
import OtpForm from "../components/otp-form";
import { useAuthStore } from "@/modules/auth/stores/auth-store";
import Button from "@/components/ui/button";
import IconGoogle from "@/components/icons/icon-google";
import { partnerApi } from "@/modules/partner";
import type { Partner } from "@/modules/partner";
import {
	PARTNER_KEY,
	getLastUsedLogin,
	saveLastUsedLogin,
	LastUsedBadge,
	PartnerBadge,
	PartnerBadgeSkeleton,
} from "@/components/auth-page-shared";

type Step = "email" | "otp";

export default function LoginPage() {
	const { login, validateOtp } = useAuthStore();
	const [step, setStep] = useState<Step>("email");
	const [sessionId, setSessionId] = useState<string | null>(null);
	const [email, setEmail] = useState<string>("");
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const searchParams = new URLSearchParams(location.search);
	const redirect = searchParams.get("redirect");
	const partnerSlug = searchParams.get("partner");

	// Use URL slug if present, otherwise fall back to saved slug
	const savedSlug = localStorage.getItem(PARTNER_KEY);
	const effectiveSlug = partnerSlug || savedSlug;

	const [partner, setPartner] = useState<Partner | null>(null);
	const [partnerLoading, setPartnerLoading] = useState(!!effectiveSlug);

	const lastUsedLogin = getLastUsedLogin();
	const lastUsedMethod = lastUsedLogin?.method;
	const savedEmail = lastUsedLogin?.method === "email" ? lastUsedLogin.email : undefined;

	// Build query string to preserve on navigation links
	const queryString = searchParams.toString();
	const signupLink = `/signup${queryString ? `?${queryString}` : ""}`;

	useEffect(() => {
		if (!effectiveSlug) return;

		partnerApi.getBySlug(effectiveSlug).then((result) => {
			if (result.success && result.data) {
				setPartner(result.data);
				localStorage.setItem(PARTNER_KEY, effectiveSlug);
			} else {
				// Partner disabled/deleted — clear saved slug
				localStorage.removeItem(PARTNER_KEY);
			}
			setPartnerLoading(false);
		});
	}, [effectiveSlug]);

	const [accountNotFound, setAccountNotFound] = useState(false);

	const handleEmailSubmit = async (data: { email: string }) => {
		setIsLoading(true);
		setError(null);
		setAccountNotFound(false);

		try {
			const payload: { email: string; redirect?: string; partner_id?: string } = { email: data.email };
			if (redirect) payload.redirect = redirect;
			if (partner) payload.partner_id = partner.id;

			const result = await login(payload);

			if (!result) {
				throw new Error("Failed to send verification code");
			}

			localStorage.removeItem(PARTNER_KEY);
			setSessionId(result.session_id);
			setEmail(data.email);
			setStep("otp");
		} catch (err) {
			if (err instanceof Error && err.message === "ACCOUNT_NOT_FOUND") {
				setAccountNotFound(true);
				setEmail(data.email);
			} else {
				setError(err instanceof Error ? err.message : "An error occurred");
			}
		} finally {
			setIsLoading(false);
		}
	};

	const handleOtpSubmit = async (code: string) => {
		if (!sessionId) return;

		setIsLoading(true);
		setError(null);

		try {
			const isValid = await validateOtp({ session_id: sessionId, code });

			if (!isValid) {
				throw new Error("Invalid verification code");
			}

			saveLastUsedLogin({ method: "email", email });
		} catch (err) {
			setError(err instanceof Error ? err.message : "An error occurred");
		} finally {
			setIsLoading(false);
		}
	};

	const handleGoogleLogin = () => {
		saveLastUsedLogin({ method: "google" });
		localStorage.removeItem(PARTNER_KEY);

		const params = new URLSearchParams();
		if (redirect) params.set("redirect", redirect);
		if (partner) params.set("partner_id", partner.id);

		const query = params.toString();
		const baseUrl = `${import.meta.env.VITE_SERVICE_CORE_URL}/auth/google/redirect`;
		window.location.href = query ? `${baseUrl}?${query}` : baseUrl;
	};

	return (
		<div className="w-full sm:w-lg flex-col rounded-lg relative z-10 p-0 sm:p-8">
			{step === "email" ? (
				<>
					<img className="mb-6 sm:mb-8 w-12 sm:w-16" src="/images/sayaboutus-logomark-light.svg" />
					<h1 className="text-lg sm:text-xl font-semibold text-primary -leading-[1%]">Log in to your account</h1>
					<p className="mt-2 sm:mt-3 text-sm sm:text-base text-tertiary max-w-[640px] mb-4 sm:mb-6">
						Collect and manage testimonials from your customers, then share them beautifully to build trust and grow your brand.
					</p>

					{partnerLoading && <PartnerBadgeSkeleton />}
					{partner && !partnerLoading && <PartnerBadge partner={partner} />}

					<EmailForm
						onSubmit={handleEmailSubmit}
						isLoading={isLoading}
						error={error}
						defaultEmail={savedEmail}
						badge={lastUsedMethod === "email" ? <LastUsedBadge className="top-[34px]" mobileClassName="right-0 top-0" /> : undefined}
					/>

					{accountNotFound && (
						<div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
							<p className="text-sm text-tertiary">
								We couldn't find an account with this email.{" "}
								<Link to={`${signupLink}${signupLink.includes("?") ? "&" : "?"}email=${encodeURIComponent(email)}`} className="text-primary hover:underline font-medium">
									Create a new account
								</Link>{" "}
								to get started.
							</p>
						</div>
					)}

					<div className="relative my-4 sm:my-6">
						<div className="absolute inset-0 flex items-center">
							<div className="w-full border-t border-gray-300"></div>
						</div>
						<div className="relative flex justify-center text-sm">
							<span className="bg-[#fafafa] px-4 text-gray-500">Or</span>
						</div>
					</div>

					<div className="relative mb-4 sm:mb-6">
						<Button variant="light" onClick={handleGoogleLogin} disabled={isLoading} className="w-full flex items-center justify-center gap-3">
							<IconGoogle />
							Log in with Google
						</Button>
						{lastUsedMethod === "google" && <LastUsedBadge className="top-1/2 -translate-y-1/2" mobileClassName="left-1/2 -translate-x-1/2 -bottom-6" />}
					</div>

					<p className="text-sm text-tertiary text-center">
						Don't have an account?{" "}
						<Link to={signupLink} className="text-primary hover:underline font-medium">
							Sign up
						</Link>
					</p>
				</>
			) : (
				<OtpForm email={email} onSubmit={handleOtpSubmit} onBack={() => setStep("email")} onResend={() => handleEmailSubmit({ email })} isLoading={isLoading} error={error} />
			)}
		</div>
	);
}
